import { writable } from 'svelte/store';
import collect from 'collect.js';
import config from '@/config.js';
import { mergeCollectionStore } from '@/Common/Collections.js';
// Models
import MarketGroup from '@/Models/MarketGroup.js';
import EventMarketGroup from '@/Models/EventMarketGroup.js';
// Stores
import ParentMarketStore from '@/Stores/Sport/ParentMarketStore.js';
import MarketStore from '@/Stores/Sport/MarketStore.js';
import SportSelectionStore from '@/Stores/Sport/SportSelectionStore.js';

/**
 * Our Store can be used 2 ways in svelte components
 * 1) ($ prefix) $StoreName to reference writable store collection with all collect.js methods available
 * 2) (Direct) StoreName.method() to call a method on the store
 */
const init = () => {
	let _store;
	let meta;
	let _parentMarketStore;

	const { subscribe, set, update } = writable(collect());

	subscribe((current) => (_store = current));

	const methods = {
		getPagination() {
			return meta && meta.pagination;
		},

		getMarketGroups(pagination = null) {
			if (!config.SPORT_ENABLED) return;

			const marketGroup = new MarketGroup();

			let results = pagination ? marketGroup.paginate(pagination.size, pagination.number) : marketGroup.get();
			return results
				.then((result) => {
					meta = result.meta;
					set(collect(result.data));
				})
				.catch((e) => {
					console.error(e);
				});
		},

		getMarketGroupsForSport(sportId, pagination = null, directReturn = false) {
			if (!config.SPORT_ENABLED) return;

			const marketGroup = new MarketGroup();
			marketGroup.resourceUrl = () => {
				return `${marketGroup.baseUrl()}/sports/${sportId}/market_groups`;
			};

			let results = pagination ? marketGroup.paginate(pagination.size, pagination.number) : marketGroup.get();
			return results
				.then((result) => {
					if (!directReturn) {
						meta = result.meta;
						set(collect(result.data));
					} else {
						return collect(result.data);
					}
				})
				.catch((e) => {
					console.error(e);
				});
		},

		getMarketGroup(id, directReturn = false) {
			if (!config.SPORT_ENABLED) return;
			if (id == undefined) return;

			const marketGroup = new MarketGroup();
			return marketGroup
				.with(['grouped_parent_markets'])
				.find(id)
				.then((result) => {
					result.grouped_parent_markets = result.grouped_parent_markets.data.sortBy('order').map((market) => {
						return {
							parent_market_id: market.parent_market_id,
							market_type: market.market_type,
							name: market.name,
							order: market.order,
							is_manual: market.is_manual
						};
					});
					if (directReturn) {
						return result;
					}
					set(result);
				});
		},

		getMarketGroupDetails(groupId, eventId) {
			if (!config.SPORT_ENABLED) return;
			if (groupId == undefined) return;

			const marketGroup = new EventMarketGroup();
			return marketGroup
				.find(groupId)
				.then((market_group) => {
					let allMarketGroups = collect();
					let allMarkets = collect();
					let allSportSelections = collect();
					// Markets
					market_group.markets.data.sortBy('order').map((market) => {
						// Selections
						market.selections.data.map((selection) => {
							selection.market_id = market.id;
							allSportSelections.push(selection);
						});
						delete market.selections;
						allMarkets.push(market);
					});
					delete market_group.markets;
					market_group.market_ids = allMarkets.pluck('id').toArray();
					market_group.sportevent_id = eventId;
					allMarketGroups.push(market_group);

					mergeCollectionStore(this, allMarketGroups);
					mergeCollectionStore(MarketStore, allMarkets);
					mergeCollectionStore(SportSelectionStore, allSportSelections);
				})
				.catch((e) => {
					console.error(e);
				});
		},

		newMarketGroup() {
			let marketGroup = new MarketGroup();
			marketGroup = marketGroup.hydrate({ id: null, name: '', sport: '', type: 'market_groups' });
			marketGroup.grouped_parent_markets = collect();
			set(marketGroup);
		},

		saveMarketGroup() {
			return _store.id
				? _store.update(preparePayload())
				: _store.create({ sport_id: _store.sport, ...preparePayload() }).then((result) => {
						update((marketGroup) => {
							marketGroup.id = result.id;
							return marketGroup;
						});
						return result;
					});
		},

		saveMarketGroupMarkets() {
			return _store.updateParentMarkets(preparePayload());
		},

		updateName(name) {
			update((marketGroup) => {
				marketGroup.name = name;
				return marketGroup;
			});
		},

		updateSelectedSport(id) {
			update((marketGroup) => {
				marketGroup.sport = id;
				return marketGroup;
			});
		},

		addParentMarket(market) {
			update((marketGroup) => {
				let nextOrder = (marketGroup.grouped_parent_markets.sortBy('order').pluck('order').last() || 0) + 1;
				// don't add the market if we already have it
				if (marketGroup.grouped_parent_markets.where('parent_market_id', market.id).count() === 0) {
					marketGroup.grouped_parent_markets.push({
						parent_market_id: market.id,
						market_type: market.market_type,
						name: market.name,
						order: nextOrder
					});
				}
				return marketGroup;
			});
		},

		addParentMarketType(marketTypeId) {
			let markets = _parentMarketStore.sortBy('order').where('market_type.id', marketTypeId);
			update((marketGroup) => {
				let nextOrder = (marketGroup.grouped_parent_markets.sortBy('order').pluck('order').last() || 0) + 1;
				markets.each((market) => {
					// don't add the market if we already have it
					if (marketGroup.grouped_parent_markets.where('parent_market_id', market.id).count() === 0) {
						marketGroup.grouped_parent_markets.push({
							parent_market_id: market.id,
							market_type: market.market_type,
							name: market.name,
							order: nextOrder
						});
						nextOrder++;
					}
				});
				return marketGroup;
			});
		},

		removeParentMarketId(id) {
			update((marketGroup) => {
				marketGroup.grouped_parent_markets = marketGroup.grouped_parent_markets.reject(
					(market) => market.parent_market_id === id
				);
				return marketGroup;
			});
			this.reorderMarkets();
		},

		removeParentMarketType(id) {
			update((marketGroup) => {
				marketGroup.grouped_parent_markets = marketGroup.grouped_parent_markets.reject(
					(market) => market.market_type.id === id
				);
				return marketGroup;
			});
			this.reorderMarkets();
		},

		swapMarketTypeOrder(fromMarketTypeId, toMarketTypeId) {
			let gpm = _store.grouped_parent_markets;
			let fromOrder = gpm.where('market_type.id', fromMarketTypeId).sortBy('order').pluck('order').first();
			let toOrder = gpm.where('market_type.id', toMarketTypeId).sortBy('order').pluck('order').first();
			gpm = gpm.sortBy('order').map((market) => {
				if (market.market_type.id === fromMarketTypeId) {
					market.order = toOrder;
					toOrder++;
				} else if (market.market_type.id === toMarketTypeId) {
					market.order = fromOrder;
					fromOrder++;
				}
				return market;
			});

			update((marketGroup) => {
				marketGroup.grouped_parent_markets = gpm;
				return marketGroup;
			});
			this.reorderMarkets();
		},

		swapMarketOrder(from, to) {
			let gpm = _store.grouped_parent_markets;
			const fromOrder = from.order;
			const toOrder = to.order;
			gpm = gpm.sortBy('order').map((market) => {
				if (market.parent_market_id === from.parent_market_id) {
					market.order = toOrder;
				} else if (market.parent_market_id === to.parent_market_id) {
					market.order = fromOrder;
				}
				return market;
			});

			update((marketGroup) => {
				marketGroup.grouped_parent_markets = gpm;
				return marketGroup;
			});
		},

		reorderMarkets() {
			update((marketGroup) => {
				let gpm = _store.grouped_parent_markets;
				const marketTypes = gpm.sortBy('order').pluck('market_type.id').unique();
				let order = 1;
				marketTypes.each((marketType) => {
					gpm
						.sortBy('order')
						.where('market_type.id', marketType)
						.map((market) => {
							market.order = order;
							order++;
							return market;
						});
				});
				return marketGroup;
			});
		},

		resetGroupedParentMarkets() {
			update((marketGroup) => {
				marketGroup.grouped_parent_markets = collect();
				return marketGroup;
			});
		},

		resetStore() {
			set(collect());
		}
	};

	function preparePayload() {
		const grouped_parent_markets = _store.grouped_parent_markets;
		return {
			name: _store.name,
			parent_markets: grouped_parent_markets
				.map((parentMarket) => {
					return {
						parent_market_id: parseInt(parentMarket.parent_market_id),
						order: parentMarket.order
					};
				})
				.toArray()
		};
	}

	ParentMarketStore.subscribe((value) => {
		_parentMarketStore = value;
	});

	return {
		subscribe,
		update,
		set,
		...methods
	};
};

export default init();
